
import { functions } from '@/firebase/functions'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'CompetitionStatsGenerator',
  props: {
    competitionId: {
      type: String,
      required: true
    },
    competitionName: {
      type: String,
      default: 'Competition'
    }
  },
  data () {
    return {
      playerLimit: 15,
      offset: 0,
      loading: false,

      alert: {
        show: false,
        type: 'info' as 'success' | 'error' | 'warning' | 'info',
        message: ''
      },

      progress: {
        show: false,
        percentage: 0,
        processed: 0,
        errors: 0,
        color: 'primary',
        message: '',
        alertType: 'info' as 'success' | 'error' | 'warning' | 'info',
        duration: '0s'
      },

      results: {
        show: false,
        competitionName: '',
        processed: 0,
        errors: 0,
        successRate: 0,
        duration: '',
        topPerformers: [] as Array<{
          playerId: string;
          totalMatches: number;
          totalGoals: number;
          winRate: number;
        }>
      },

      pagination: {
        hasMore: false,
        nextOffset: 0
      }
    }
  },

  methods: {
    async generateStatistics () {
      if (!this.competitionId) {
        this.showAlert('warning', 'No competition ID provided')
        return
      }

      this.loading = true
      this.progress.show = true
      this.results.show = false
      this.progress.percentage = 0
      this.progress.processed = 0
      this.progress.errors = 0
      this.progress.message = 'Starting statistics generation...'
      this.progress.color = 'primary'
      this.progress.alertType = 'info'
      this.progress.duration = '0s'

      const startTime = Date.now()
      const startInterval = setInterval(() => {
        const elapsed = Math.round((Date.now() - startTime) / 1000)
        this.progress.duration = `${elapsed}s`
      }, 1000)

      try {
        const generateCompetitionStats = httpsCallable(functions, 'generateCompetitionPlayerStatistics')

        const result = await generateCompetitionStats({
          competitionId: this.competitionId,
          limit: this.playerLimit,
          offset: this.offset
        })

        const data = result.data as any

        // Update progress
        this.progress.percentage = 100
        this.progress.processed = data.results.processed
        this.progress.errors = data.results.errors
        this.progress.message = data.message
        this.progress.color = data.results.errors > 0 ? 'warning' : 'success'
        this.progress.alertType = data.results.errors > 0 ? 'warning' : 'success'

        // Show results
        const endTime = Date.now()
        const duration = Math.round((endTime - startTime) / 1000)

        this.results.show = true
        this.results.competitionName = this.competitionName
        this.results.processed = data.results.processed
        this.results.errors = data.results.errors
        this.results.successRate = data.results.processed > 0
          ? Math.round(((data.results.processed - data.results.errors) / data.results.processed) * 100)
          : 0
        this.results.duration = `${duration} seconds`
        this.results.topPerformers = data.results.players
          .sort((a: any, b: any) => b.totalGoals - a.totalGoals)
          .slice(0, 10)

        // Update pagination info
        if (data.pagination) {
          this.pagination.hasMore = data.pagination.hasMore
          this.pagination.nextOffset = data.pagination.nextOffset
        }

        this.showAlert('success', `Statistics generated successfully for ${data.results.processed} players`)
      } catch (error: any) {
        console.error('Error generating statistics:', error)
        this.progress.message = 'Generation failed'
        this.progress.color = 'error'
        this.progress.alertType = 'error'
        this.showAlert('error', `Failed to generate statistics: ${error.message}`)
      } finally {
        this.loading = false
        clearInterval(startInterval)
      }
    },

    generateNextBatch () {
      this.offset = this.pagination.nextOffset
      this.generateStatistics()
    },

    showAlert (type: 'success' | 'error' | 'warning' | 'info', message: string) {
      this.alert = {
        show: true,
        type,
        message
      }
    }
  }
}
